import React from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import RemoveIcon from '@material-ui/icons/Remove'
import moment from 'moment'
import cn from 'classnames'
import AttachIcon from '@material-ui/icons/AttachFile'
import CommentIcon from '@material-ui/icons/MessageOutlined'
import Tooltip from '@material-ui/core/Tooltip'
import Link from '@material-ui/core/Link'
import isEqual from 'lodash/isEqual'
import ArrowIcon from '@material-ui/icons/ExpandMore'
import ListFromTable from 'components/common/ListFromTable'
import ColumnCollection from 'types/table/ColumnCollection'
import Column from 'types/table/Column'
import ActionCollection from 'types/table/ActionCollection'
import Action from 'types/table/Action'
import {
  openEventFormModal,
  fetchEventDataList,
  fetchEventDataItem,
  clearEventDataItem,
  deleteEvent,
  setPsfs,
} from 'reducers/logbook/reducer'
import { updateProfileLogbookFilters } from 'reducers/profile/reducer'
import withPromise from 'helpers/withPromise'
import { COMPULSORY_FIELDS } from 'constants/logbookFilters'
import { EVENT_CATEGORY_COLORS } from 'helpers/logbook'
import licenseeDigitFormat from 'helpers/licenseeDigitFormat'
import Psfs from 'lib/psfs/Psfs'
import Sort from 'lib/psfs/Sort'
import messages from './translate'

class EventList extends ListFromTable {
  constructor(props) {
    super(props)
    this.deleteEntity = this.deleteEvent
    this.asyncData = true
    this.loadingData = true

    this.state = {
      ...this.state,
      showSlaveEvents: null,
      isDense: props.logbookFilters.densePadding,
      rowId: null,
      categoryColors: {},
    }

    this.init()
  }

  init = () => {
    const { storePsfs, setPsfs } = this.props

    this.psfs = new Psfs(storePsfs.path)
    this.historyPush = path => {
      this.setState({ pathname: path })
      setPsfs(new Psfs(path))
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { storePsfs, tableData, history } = this.props
    const { storePsfs: nextPsfs, tableData: nextTableData, loader } = nextProps

    this.loadingData = loader

    if (!isEqual(tableData, nextTableData)) {
      this.loadingData = false
    }

    if (storePsfs !== nextPsfs) {
      history.push(nextPsfs.path)
    }

    return super.shouldComponentUpdate(nextProps, nextState, nextContext)
  }

  fetchEventDataList = (site, date) => {
    const { fetchEventDataList, getQueryParams, storePsfs } = this.props
    const { startDate, endDate } = date.toJS()
    const queryParams = {
      psfs: storePsfs,
      params: {
        site,
        from: startDate.format('YYYY-MM-DD'),
        to: endDate.format('YYYY-MM-DD'),
      },
    }

    getQueryParams(queryParams)
    fetchEventDataList(queryParams)
  }

  handleCompactClick = () => {
    const { updateProfileLogbookFilters } = this.props

    this.setState(
      prevState => ({
        isDense: !prevState.isDense,
      }),
      () => {
        updateProfileLogbookFilters({
          logbook_filters: {
            densePadding: this.state.isDense,
          },
        })
      },
    )
  }

  deleteEvent = params => {
    const {
      activeSite,
      activeDateRange,
      deleteEvent,
      onClosePanel,
    } = this.props

    withPromise(deleteEvent, params).then(() => {
      this.fetchEventDataList(activeSite, activeDateRange)
      onClosePanel()
    })
  }

  attachPreview = e => {
    e.stopPropagation()
  }

  getTableProps() {
    const {
      intl: { formatMessage },
      onTableRowClick,
      clearEventDataItem,
      fetchEventDataItem,
      activeRow,
      storePsfs,
      isLoadSiteList,
      tableData,
      dataFields,
    } = this.props
    const { showSlaveEvents, isDense, rowId } = this.state
    const { id } = activeRow.toJS()
    const tableDataList = tableData.toJS()
    const tableDataLength =
      Object.prototype.hasOwnProperty.call(tableDataList, 'count') &&
      tableDataList.count
    const baseColumnLength = COMPULSORY_FIELDS.length + 2
    const tableColumnLength = dataFields.length + baseColumnLength

    return {
      title: '',
      className: cn('logbook-table', {
        'vertical-scroll': tableDataLength > 10,
      }),
      showFilterBar: false,
      sorting: true,
      paging: true,
      dense: {
        enabled: true,
        state: isDense,
        handler: this.handleCompactClick,
      },
      hasStorePsfs: true,
      storePsfs,
      isLoadSiteList,
      selectedRowId: rowId,
      noDataMessage: formatMessage(messages.noRecordedEvents),
      showAdditionalRows: showSlaveEvents,
      combinedColumns: tableColumnLength,
      onRowClick: row => {
        onTableRowClick()
        this.setState({ rowId: row.id })

        if (row.id !== id) {
          clearEventDataItem()
          fetchEventDataItem({ id: row.id })
        }
      },
    }
  }

  onCellClick(e, action) {
    e.stopPropagation()
    const { showSlaveEvents } = this.state
    let slaveId = action

    if (showSlaveEvents === action) {
      slaveId = null
    }
    this.setState({
      showSlaveEvents: slaveId,
    })
  }

  getConfirmDialogProps() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.deleteEvent),
      message: formatMessage(messages.deleteEventMessage),
    }
  }

  getColumns() {
    const {
      licensee,
      dataFields,
      storePsfs,
      intl: { formatMessage },
    } = this.props
    const { showSlaveEvents, categoryColors } = this.state
    const {
      _field,
      _order: [orderType],
    } = storePsfs.sortObj
    const getResultByType = row => {
      const { result } = row
      const isResultDateValid = moment(result, 'YYYY-MM-DD', true).isValid()

      if (Array.isArray(result)) {
        return result.map(item => formatMessage({ id: item })).join(', ')
      }
      if (isResultDateValid) {
        return moment(result).format('DD/MM/YYYY')
      }
      if (typeof result === 'string' && result.includes('_')) {
        return formatMessage({ id: result })
      }
      if (
        result &&
        typeof result === 'object' &&
        Object.prototype.hasOwnProperty.call(result, 'uri')
      ) {
        const { uri, originalName } = result

        return (
          <Link
            href={uri}
            title={originalName}
            target="_blank"
            rel="noopener noreferrer"
            className="file-link"
            onClick={this.attachPreview}>
            <AttachIcon />
          </Link>
        )
      }

      return licenseeDigitFormat(result, licensee) || result === 0
        ? result.toString()
        : ''
    }
    const columns = new ColumnCollection()
    const tableFields = COMPULSORY_FIELDS.concat(dataFields).sort(
      (a, b) => a.id - b.id,
    )
    const eventCategory = {}

    const getSorting = storeName => {
      const sorting = {}

      if (_field && _field === storeName) {
        sorting.defaultSort = _field
        sorting.defaultSortOrder = Sort[orderType.toUpperCase()]
      }

      return sorting
    }

    const arrowIcon = action => (
      <div
        onClick={e => this.onCellClick(e, action)}
        className={cn('logbook-event-item-box', {
          active: showSlaveEvents === action || showSlaveEvents === 'show all',
        })}>
        <ArrowIcon />
      </div>
    )

    columns.add(
      new Column('master_event_arrow', arrowIcon('show all'), {
        sorting: false,
        cellClassName: 'logbook-event-arrow-cell',
        render: rowData =>
          rowData.slaves && rowData.slaves.length > 0 && arrowIcon(rowData.id),
      }),
    )

    tableFields.forEach(item => {
      const sortParams = getSorting(item.storeName)
      columns.add(
        new Column(
          item.storeName,
          formatMessage(messages[item.translateName]),
          {
            sorting: true,
            ...sortParams,
            render: rowData => {
              if (item.storeName === 'comment') {
                const commentText = rowData.comment

                if (commentText) {
                  return (
                    <Tooltip
                      placement="top-start"
                      title={commentText}
                      classes={{
                        tooltip: 'custom-tooltip',
                      }}>
                      <CommentIcon color="red" />
                    </Tooltip>
                  )
                }

                return null
              }

              if (item.storeName === 'category') {
                if (!Object.keys(categoryColors).includes(rowData.category)) {
                  const { length } = Object.keys(eventCategory)

                  eventCategory[rowData.category] =
                    EVENT_CATEGORY_COLORS[length]
                  this.setState({
                    categoryColors: eventCategory,
                  })
                }

                return (
                  <div
                    className="logbook-event-category"
                    style={{
                      backgroundColor: categoryColors[rowData.category],
                    }}>
                    {formatMessage({ id: rowData[item.translateName] })}
                  </div>
                )
              }

              if (item.type === 'event') {
                return formatMessage({ id: rowData[item.translateName] })
              }
              if (item.type === 'obj') {
                return rowData[item.storeName] ? (
                  rowData[item.storeName].name
                ) : (
                  <RemoveIcon />
                )
              }
              if (item.type === 'result') {
                return getResultByType(rowData)
              }
              if (item.type === 'event_data') {
                return rowData[item.storeName]
              }
              if (item.type === 'file') {
                const { originalName = null, uri = null } =
                  rowData[item.storeName] || {}

                return rowData[item.storeName] && originalName ? (
                  <Link
                    href={uri}
                    title={originalName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="file-link"
                    onClick={this.attachPreview}>
                    <AttachIcon />
                  </Link>
                ) : (
                  <RemoveIcon />
                )
              }
            },
          },
        ),
      )
    })

    return columns
  }

  getActions() {
    const {
      intl: { formatMessage },
      fetchEventDataItem,
      onEditEvent,
      activeRow,
    } = this.props
    const actions = new ActionCollection()
    const { id } = activeRow.toJS()

    actions.add(
      new Action(
        'edit',
        formatMessage(messages.eventEdit),
        row => {
          onEditEvent()

          if (id !== row.id) {
            fetchEventDataItem({ id: row.id })
          }
        },
        {
          className: 'action-edit',
        },
      ),
    )
    actions.add(
      new Action('delete', formatMessage(messages.deleteEvent), row => {
        this.onOpenConfirmDialog({ id: row.id })
      }),
    )

    return actions
  }
}

export default connect(
  state => ({
    isOpenEventFormModal: state.getIn(['logbook', 'isOpenEventFormModal']),
    tableData: state.getIn(['logbook', 'tableData']),
    loader: state.getIn(['logbook', 'isLoadTable']),
    activeSite: state.getIn(['profile', 'siteId']),
    logbookFilters: state.getIn(['profile', 'profile', 'logbookFilters']),
    activeDateRange: state.getIn(['sensor', 'dateRange']),
    activeRow: state.getIn(['logbook', 'eventForm']),
    storePsfs: state.getIn(['logbook', 'storePsfs']),
    isLoadSiteList: state.getIn(['monitorsAndAlerts', 'isLoadSiteList']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    openEventFormModal,
    deleteEvent,
    fetchEventDataList,
    fetchEventDataItem,
    clearEventDataItem,
    setPsfs,
    updateProfileLogbookFilters,
  },
)(injectIntl(EventList))
